import { FC, useState } from "react";
import { Button, Modal } from "@monolith-forensics/monolith-ui";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import { StorageAPI } from "../storage.api";
import LoggingAPI from "@/api/logging";
import { useSnackbar, VariantType } from "notistack";
import { usePermissions } from "@/hooks/usePermissions";
import { StorageItemForDelete } from "../storage.types";

interface DeleteStorageModalProps {
  open: boolean;
  onClose: () => void;
  onDelete: (deletedItem: StorageItemForDelete) => void;
  storageInfo: StorageItemForDelete | null;
}

export const DeleteStorageModal: FC<DeleteStorageModalProps> = ({
  open,
  onClose,
  onDelete,
  storageInfo,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = usePermissions();
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      if (storageInfo?.storage_id) {
        const result = await StorageAPI.delete({
          storage_id: storageInfo.storage_id,
        });
        // The API layer resolves (never rejects) on HTTP errors — treat a
        // { success: false } result as failure.
        if (result?.success === false) {
          throw new Error("Failed to delete storage item");
        }

        enqueueSnackbar(`Storage Item Deleted.`, {
          variant: "success" as VariantType,
        });

        LoggingAPI.createLog({
          user_id: currentUser.user_id,
          message: `Deleted storage item: ${storageInfo.storage_number}`,
          event: "storage:delete",
          object_type: "storage",
          object_uuid: storageInfo.uuid,
        }).catch(() => {});

        // Case activity log — the legacy delete modal recorded deletions of
        // case-assigned items in the case's activity log; keep that trail.
        if (storageInfo.case_id) {
          LoggingAPI.logActivity(
            storageInfo.case_id,
            currentUser.user_id,
            `Deleted storage item ${storageInfo.storage_number || ""}`
          ).catch(() => {});
        }

        // Run the parent callback outside the API error path — the delete
        // already succeeded, so a UI-side failure here must not surface as a
        // "delete failed" error or leave the modal open.
        try {
          onDelete?.(storageInfo);
        } catch {
          /* swallow — delete already committed on the server */
        }
      }
      onClose();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        `An error occurred while deleting storage item "${
          storageInfo?.storage_number || ""
        }". Please try again.`,
        { variant: "error" as VariantType }
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Title>Delete Storage Item</Modal.Title>
      <div style={{ fontWeight: 500, margin: "20px 0px" }}>
        Are you sure you want to delete storage item{" "}
        <span style={{ fontWeight: 800 }}>"{storageInfo?.storage_number}"</span>
        ?<p>This action cannot be undone.</p>
      </div>
      <ButtonMenu>
        <Button variant="subtle" size="xs" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          size="xs"
          loading={deleting}
          disabled={deleting}
          onClick={handleDelete}
        >
          Delete Storage
        </Button>
      </ButtonMenu>
    </Modal>
  );
};
